import { Skill } from "../types/prisma-enums";
import type { GameContent, Item, Level } from "./types";

/**
 * Turo's World — General Awareness · games-spec.md §6.
 *
 * Turo asks about the world the child already knows — *"Which one do we use to
 * eat?"*, *"Where does a fish live?"* — and the child **points** to the answer,
 * then **raises both arms** to confirm.
 *
 * 🔴 THE PALATE-CLEANSER. `exertion: 'low'`, sitting between the two
 * high-exertion games in a session (games-spec §2's alternating rhythm). It also
 * *"produces early wins and gives the teacher a read on vocabulary and
 * comprehension without a reading task"* — which is the reason the distractors
 * below are plausible rather than silly.
 *
 * 🔴 EXACTLY THREE CHOICES EVERYWHERE, and that is a deliberate narrowing.
 * §6 says *"3–4 large pictures"*, but the camera control is `pick: -1|0|1` —
 * three zones (OD-29). Authoring four would make a tap child choose from four
 * while a camera child chose from three: **the same item at two difficulties
 * depending on how the child plays**, which breaks I3's "same either way" at the
 * content layer where no reward function can see it.
 *
 * ⚠️ THE MALAYALAM COLUMN OF THIS TABLE IS GONE (ADR-044).
 */

/**
 * 🔴 THE PICTURE VOCABULARY — every choice must be a real thing a Kerala child
 * knows, and the test enforces it.
 *
 * games-spec §6's value is that a teacher can *read a child's vocabulary* from
 * this game. That only works if the distractors are **plausible**: "which one do
 * we use to eat?" with *spoon / cup / shoe* tells you something; with *spoon /
 * cloud / elephant* it tells you nothing, because a child can eliminate by
 * absurdity without knowing the answer.
 *
 * ⚠️ THE DOMAIN IS KERALA, NOT A STOCK-PHOTO LIBRARY — an auto-rickshaw and a
 * fish market, not a taxi and a supermarket. A child whose "everyday objects"
 * come from somewhere else is being tested on cultural distance rather than
 * general awareness. Worth an early-years teacher's eye before pilot.
 */
export const PICTURES = [
  // everyday objects
  "spoon",
  "cup",
  "plate",
  "ball",
  "shoe",
  "comb",
  "soap",
  "towel",
  "chair",
  "bag",
  // animals and their homes
  "fish",
  "bird",
  "cow",
  "dog",
  "cat",
  "elephant",
  "nest",
  "pond",
  "shed",
  "kennel",
  // food, body, clothes
  "rice",
  "banana",
  "mango",
  "coconut",
  "hand",
  "foot",
  "eye",
  "ear",
  "shirt",
  "skirt",
  // day and night, weather, growing
  "sun",
  "moon",
  "star",
  "cloud",
  "rain",
  "umbrella",
  "raincoat",
  "seed",
  "plant",
  "flower",
  "tree",
  // transport and community places
  "bus",
  "auto-rickshaw",
  "boat",
  "bicycle",
  "temple",
  "clinic",
  "school",
  "market",
  "post-office",
] as const;

export type Picture = (typeof PICTURES)[number];

/** 🔴 Three choices, always — see the header. */
const item = (
  id: string,
  en: string,
  audioKey: string,
  target: Picture,
  choices: readonly [Picture, Picture, Picture],
): Item => ({
  id,
  prompt: { en },
  audioKey,
  target,
  choices: [...choices],
});

/* ── L1 · everyday objects ───────────────────────────────────────────────────
 * Spoon, cup, ball, shoe. The things in a child's own house, so the first
 * question they are ever asked in this game is one they can already answer.
 */
const L1: Level = {
  level: 1,
  /*
   * ⚠️ NAMED FOR WHAT PLAYS. `TwRound` plays a copy-the-pose warm-up at L1, Turo's
   * greeting lessons at L2 and his kind-words lessons at L3 — never the quiz
   * items below. The old names ("Things we use", "Where do they live?", "Us and
   * our day") told teachers about questions no child is asked.
   */
  name: { en: "Move with Turo" },
  objective: {
    en: "Copy Turo's moves and hold each one",
  },
  ncf: ["C-6.1"],
  itemCount: 6,
  motorAction: { kind: "armsUp" },
  items: [
    item("tw.l1.a", "Which one do we use to eat?", "tw_l1_a", "spoon", [
      "spoon",
      "comb",
      "shoe",
    ]),
    item("tw.l1.b", "Which one do we drink from?", "tw_l1_b", "cup", [
      "cup",
      "ball",
      "towel",
    ]),
    item("tw.l1.c", "Which one do we wear on our feet?", "tw_l1_c", "shoe", [
      "shoe",
      "plate",
      "comb",
    ]),
    item("tw.l1.d", "Which one do we play with?", "tw_l1_d", "ball", [
      "ball",
      "soap",
      "chair",
    ]),
    item("tw.l1.e", "Which one do we sit on?", "tw_l1_e", "chair", [
      "chair",
      "cup",
      "bag",
    ]),
    item("tw.l1.f", "Which one do we wash with?", "tw_l1_f", "soap", [
      "soap",
      "spoon",
      "ball",
    ]),
    item("tw.l1.g", "Which one do we comb our hair with?", "tw_l1_g", "comb", [
      "comb",
      "plate",
      "shoe",
    ]),
    item("tw.l1.h", "Which one do we dry ourselves with?", "tw_l1_h", "towel", [
      "towel",
      "chair",
      "cup",
    ]),
    item("tw.l1.i", "Which one do we eat from?", "tw_l1_i", "plate", [
      "plate",
      "soap",
      "bag",
    ]),
    item("tw.l1.j", "Which one do we carry things in?", "tw_l1_j", "bag", [
      "bag",
      "towel",
      "ball",
    ]),
    item("tw.l1.k", "Which one do we use to eat?", "tw_l1_k", "spoon", [
      "spoon",
      "chair",
      "soap",
    ]),
    item("tw.l1.l", "Which one do we wear on our feet?", "tw_l1_l", "shoe", [
      "shoe",
      "cup",
      "bag",
    ]),
  ],
};

/* ── L2 · animals & their homes ──────────────────────────────────────────────
 * "Where does a fish live?" Animals a child has actually seen — a cow, a crow,
 * a pond — rather than a zoo set they know only from a book.
 */
const L2: Level = {
  level: 2,
  name: { en: "Saying hello" },
  objective: {
    en: "Greet a friend, a teacher and family the way Turo shows",
  },
  ncf: ["C-6.1", "C-11.1"],
  itemCount: 6,
  motorAction: { kind: "armsUp" },
  items: [
    item("tw.l2.a", "Where does a fish live?", "tw_l2_a", "pond", [
      "pond",
      "nest",
      "shed",
    ]),
    item("tw.l2.b", "Where does a bird live?", "tw_l2_b", "nest", [
      "nest",
      "pond",
      "kennel",
    ]),
    item("tw.l2.c", "Where does a cow live?", "tw_l2_c", "shed", [
      "shed",
      "nest",
      "pond",
    ]),
    item("tw.l2.d", "Where does a dog live?", "tw_l2_d", "kennel", [
      "kennel",
      "pond",
      "nest",
    ]),
    item("tw.l2.e", "Which one swims?", "tw_l2_e", "fish", [
      "fish",
      "cat",
      "cow",
    ]),
    item("tw.l2.f", "Which one flies?", "tw_l2_f", "bird", [
      "bird",
      "dog",
      "fish",
    ]),
    item("tw.l2.g", "Which one gives us milk?", "tw_l2_g", "cow", [
      "cow",
      "bird",
      "fish",
    ]),
    item("tw.l2.h", "Which one is the biggest?", "tw_l2_h", "elephant", [
      "elephant",
      "cat",
      "bird",
    ]),
    item("tw.l2.i", "Which one says meow?", "tw_l2_i", "cat", [
      "cat",
      "fish",
      "cow",
    ]),
    item("tw.l2.j", "Which one guards the house?", "tw_l2_j", "dog", [
      "dog",
      "fish",
      "bird",
    ]),
    item("tw.l2.k", "Where does a bird live?", "tw_l2_k", "nest", [
      "nest",
      "shed",
      "pond",
    ]),
    item("tw.l2.l", "Which one swims?", "tw_l2_l", "fish", [
      "fish",
      "elephant",
      "dog",
    ]),
  ],
};

/* ── L3 · food, body, clothes; day & night ──────────────────────────────────── */
const L3: Level = {
  level: 3,
  name: { en: "Kind words" },
  objective: {
    en: "Kind words, and what to say from morning to night",
  },
  ncf: ["C-6.1", "C-6.2"],
  itemCount: 6,
  motorAction: { kind: "armsUp" },
  items: [
    /*
     * 🔴 THE WHY-QUESTIONS LIVE HERE NOW (ADR-037). games-spec §6 puts "simple
     * why-questions" at the TOP level — the comprehension step above the what
     * and where of recall. Collapsing five levels into three moved the top from
     * L5 to L3; the rule did not change, only where it lands.
     */
    item("tw.l3.why1", "Why do we wear a raincoat?", "tw_l3_why1", "rain", [
      "rain",
      "sun",
      "moon",
    ]),
    item("tw.l3.why2", "Why do we plant a seed?", "tw_l3_why2", "tree", [
      "tree",
      "shoe",
      "cup",
    ]),
    item("tw.l3.a", "Which one do we see with?", "tw_l3_a", "eye", [
      "eye",
      "foot",
      "hand",
    ]),
    item("tw.l3.b", "Which one do we hear with?", "tw_l3_b", "ear", [
      "ear",
      "hand",
      "foot",
    ]),
    item("tw.l3.c", "Which one do we walk with?", "tw_l3_c", "foot", [
      "foot",
      "eye",
      "ear",
    ]),
    item("tw.l3.d", "Which one comes out at night?", "tw_l3_d", "moon", [
      "moon",
      "sun",
      "cloud",
    ]),
    item("tw.l3.e", "Which one shines in the day?", "tw_l3_e", "sun", [
      "sun",
      "moon",
      "star",
    ]),
    item("tw.l3.f", "Which one do we eat every day?", "tw_l3_f", "rice", [
      "rice",
      "soap",
      "towel",
    ]),
    item("tw.l3.g", "Which one grows on a tall tree?", "tw_l3_g", "coconut", [
      "coconut",
      "rice",
      "shirt",
    ]),
    item("tw.l3.h", "Which one is a fruit?", "tw_l3_h", "banana", [
      "banana",
      "plate",
      "comb",
    ]),
    item("tw.l3.i", "Which one do we wear on top?", "tw_l3_i", "shirt", [
      "shirt",
      "shoe",
      "bag",
    ]),
    item("tw.l3.j", "Which one do we hold things with?", "tw_l3_j", "hand", [
      "hand",
      "ear",
      "eye",
    ]),
    item("tw.l3.k", "Which one is sweet and yellow?", "tw_l3_k", "mango", [
      "mango",
      "rice",
      "coconut",
    ]),
    item("tw.l3.l", "Which one twinkles at night?", "tw_l3_l", "star", [
      "star",
      "sun",
      "cloud",
    ]),
  ],
};
export const TUROS_WORLD: GameContent = {
  id: "turos-world",
  category: "GENERAL_AWARENESS",
  contentSkill: Skill.GENERAL_AWARENESS,
  /**
   * 🔴 One motor skill. Raising both arms is coordination; it is not balance
   * (the child is not moving their base) and not body awareness in the sense a
   * jump exercises. games-spec §6 names exactly one, and claiming more would put
   * a competency on a report card this game does not exercise.
   */
  motorSkills: [Skill.COORDINATION],
  emoji: "🌍",
  /**
   * 🔴 LOW, BY DESIGN — *"the palate-cleanser between the two high-exertion
   * games"*. The pacer alternates exertion across a session (games-spec §2), so
   * this is a scheduling fact, not a description, and §3.6's play-zone prompt
   * does not run.
   */
  exertion: "low",
  levels: [L1, L2, L3],
};
